import React, { Ref } from 'react';
import styled from '@emotion/styled';
import { ExtraFieldsSupportedObject } from '../../../../components/ExtraFields';
import { ExtraFieldsFormIds } from '../../../../components/ExtraFields/types';
import {
  DateAndTimeIntervalType,
  TimeEntryItem,
  TimeEntryReadOnlyReason,
} from '../logic';
import { useTranslation } from 'react-i18next';
import { ValidationResult } from '../logic/validation';
import ButtonLink from '../components/ButtonLink';
import { Actions } from './actions';
import TimeEntryDumbContent from './TimeEntryDumbContent';
import TimeEntryActions from '../components/TimeEntryActions';
import StatusBadge from '../components/StatusBadge';
import { StatusDotVariant } from '../../../../components/StatusDot';
import { TimeEntryWorkingTimeIndicator } from './TimeEntryWorkingTimeIndicator';
import { useAutoBreaksLogic } from '../logic/useAutoBreaksLogic';
import { AutoBreaksRules } from '../../../../components/AutoBreaks/shared';
import { isLegalTimeEntryStats } from '../../../../components/AutoBreaks/shared/autoBreaksCalculation/helpers';
import { PendingChangesBadge } from './PendingChangesBadge';
import { CostCenterProps } from '..';
import { WarningModal } from '../../components/WarningModal/WarningModal';
import DayProjectTimeEntriesForm, {
  DayProjectTimeEntriesFormRef,
} from '../components/ProjectDayTimeEntriesForm/DayProjectTimeEntriesForm';
import { ExtraFieldsAreaSimpleRef } from '../../../../components/ExtraFields';
import {
  ProjectWithStatus,
  TimeEntryProps,
  ProjectTimeEntry,
  ProjectDayTimeTracked,
} from '../../../Projects/types';
import { TimeEntryStats } from '../../../../components/AutoBreaks/shared';

type Props = {
  timeEntry: TimeEntryItem;
  costCenters: readonly CostCenterProps[];
  readonly: boolean;
  readonlyReasons: TimeEntryReadOnlyReason[];
  readonlyIntervalToCompare?: DateAndTimeIntervalType;
  notesVisible: boolean;
  costCenterVisible: boolean;
  addNotesButtonVisible: boolean;
  addAssignCostCenterButtonVisible: boolean;
  isDeleteBreakAllowed: boolean;
  isEditNotesAllowed: boolean;
  isDeleteTimeEntryAllowed: boolean;
  forceShowEndDate: boolean;
  validationErrors: ValidationResult;
  dispatch: React.Dispatch<Actions>;
  onTimeEntryDelete?: () => void;
  isTimeEntryDeleting?: boolean;
  autoBreakRules: AutoBreaksRules | null;
  isDeleteChangeRequestAllowed?: boolean;
  onDeleteChangeRequestClick?: () => void;
  onAddNewTimeEntryClick: () => void;
  isLastEntry: boolean;
  newTimeEntry: boolean;
  projects?: ProjectWithStatus[];
  timeEntriesForDay?: TimeEntryProps[];
  showTimeEntries?: boolean;
  restrictedFutureEntries?: boolean;
  onProjectTimeEntriesMinutesChange?: (
    timeEntryId: string | null,
    minutes: number,
  ) => void;
  isTimeTrackingWithProjects: boolean;
  restrictionReason: string | null;
  showRestrictedWarningModal: boolean;
  onShowRestrictedWarningModal: (show: boolean) => void;
  onAddBreak: () => void;
  mappedProjectTimeEntries: ProjectDayTimeTracked[];
  totalProjectTimeEntriesMinutes: number;
  currentProjectTimeEntriesMinutes: number | null;
  onProjectTimeEntriesMinutesChangeInternal: (minutes: number) => void;
  onSaveProjectTimeEntries: (timesTracked: ProjectTimeEntry[]) => Promise<void>;
  projectEntriesFormRef?: Ref<DayProjectTimeEntriesFormRef>;
  extraFieldsRef?: Ref<ExtraFieldsAreaSimpleRef>;
  isSavingProjectTimeEntries: boolean;
  showProjectTimeEntriesForm: boolean;
  onShowProjectTimeEntriesForm: (show: boolean) => void;
  onConnectToProjects: () => void;
  onCancelProjectTimeEntries: () => void;
  canShowProjectTimeEntries: boolean;
  showConnectToProjectsButton: boolean;
  timeEntryStats: TimeEntryStats;
  initialHours?: number;
  onProjectEntriesChange?: (entries: ProjectTimeEntry[]) => void;
  projectEntriesResetKey?: string | number;
  employeeId: string;
  canEditExtraFields: boolean;
  extraFieldsObjectApiName: ExtraFieldsSupportedObject;
  extraFieldsFormId: ExtraFieldsFormIds;
};

const TopRightContainer = styled.div({
  position: 'absolute',
  right: 0,
  marginRight: 10,
  zIndex: 1,
});

const TimeEntryActionsContainer = styled.div({
  marginRight: 5,
});

const TimeEntryDumb: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    timeEntry,
    readonly,
    readonlyReasons,
    addNotesButtonVisible,
    isEditNotesAllowed,
    addAssignCostCenterButtonVisible,
    isDeleteTimeEntryAllowed,
    isTimeEntryDeleting,
    onTimeEntryDelete,
    dispatch,
    autoBreakRules,
    isDeleteChangeRequestAllowed,
    onDeleteChangeRequestClick,
    onAddNewTimeEntryClick,
    isLastEntry,
    newTimeEntry,
    projects,
    timeEntriesForDay,
    showTimeEntries,
    restrictedFutureEntries,
    isTimeTrackingWithProjects,
    restrictionReason,
    showRestrictedWarningModal,
    onShowRestrictedWarningModal,
    onAddBreak,
    mappedProjectTimeEntries,
    totalProjectTimeEntriesMinutes,
    currentProjectTimeEntriesMinutes,
    onProjectTimeEntriesMinutesChangeInternal,
    onSaveProjectTimeEntries,
    projectEntriesFormRef,
    isSavingProjectTimeEntries,
    showProjectTimeEntriesForm,
    onShowProjectTimeEntriesForm,
    onConnectToProjects,
    onCancelProjectTimeEntries,
    canShowProjectTimeEntries,
    showConnectToProjectsButton,
    timeEntryStats,
    initialHours,
    onProjectEntriesChange,
    projectEntriesResetKey,
  } = props;

  const { t } = useTranslation();
  const isChangeRequest = timeEntry.changeRequestId !== null;

  const handleDelete = () => onTimeEntryDelete && onTimeEntryDelete();

  const readonlyReasonBadge = (readonlyReasons: TimeEntryReadOnlyReason[]) => {
    let label = '';
    let variant: StatusDotVariant = 'success';
    if (readonlyReasons.includes('payrollCompleted')) {
      label = t('timeTracking.timeSheet.payrollCompleted');
      variant = 'success';
    } else if (readonlyReasons.includes('payrollLocked')) {
      label = t('timeTracking.timeSheet.payrollLocked');
      variant = 'info';
    } else if (readonlyReasons.includes('timeSheetApproved')) {
      label = t('timeTracking.timeSheet.timeSheetApproved');
      variant = 'info';
    }
    return <StatusBadge label={label} variant={variant} />;
  };

  const { validationPlusAutoBreaksErrors } = useAutoBreaksLogic(props);

  const isTracking = timeEntry.end === null;
  const isFixBreaksVisible =
    autoBreakRules &&
    !isLegalTimeEntryStats(timeEntryStats) &&
    !readonly &&
    !isTracking;

  const showAddNewTimeEntryButton =
    isLastEntry && !isTracking && (isChangeRequest || !newTimeEntry);

  const hasProjectTimeEntries = mappedProjectTimeEntries.length > 0;

  const handleProjectTimeEntriesMinutesChange = (minutes: number) => {
    onProjectTimeEntriesMinutesChangeInternal(minutes);
  };

  const handleSaveProjectTimeEntries = async (
    timesTracked: ProjectTimeEntry[],
  ) => {
    await onSaveProjectTimeEntries(timesTracked);
    if (timesTracked.length > 0) {
      onShowProjectTimeEntriesForm(true);
    } else {
      onShowProjectTimeEntriesForm(false);
    }
  };

  return (
    <div>
      <WarningModal
        visible={showRestrictedWarningModal}
        restrictionReason={restrictionReason}
        onClose={() => onShowRestrictedWarningModal(false)}
      />
      <TopRightContainer>
        {isChangeRequest && (
          <PendingChangesBadge
            isDeleteAllowed={isDeleteChangeRequestAllowed ?? false}
            onDeleteClick={onDeleteChangeRequestClick}
          />
        )}
        {!!readonlyReasons.length && readonlyReasonBadge(readonlyReasons)}
        {isDeleteTimeEntryAllowed && !isChangeRequest && (
          <TimeEntryActionsContainer role="button">
            <TimeEntryActions
              onDelete={handleDelete}
              isProcessing={isTimeEntryDeleting}
            />
          </TimeEntryActionsContainer>
        )}
      </TopRightContainer>
      <TimeEntryDumbContent
        {...props}
        readonly={readonly}
        validationErrors={validationPlusAutoBreaksErrors}
      />

      <div className="d-flex flex-wrap justify-content-between">
        <div>
          {!readonly && (
            <>
              <ButtonLink
                title={t('timeTrackingNew.dayCard.addBreak')}
                onClick={onAddBreak}
              />

              {addNotesButtonVisible && isEditNotesAllowed && (
                <ButtonLink
                  className="ms-3"
                  title={t('timeTrackingNew.dayCard.addNotes')}
                  onClick={() => dispatch({ type: 'addNotes' })}
                />
              )}

              {addAssignCostCenterButtonVisible && (
                <ButtonLink
                  className="ms-3"
                  title={t('timeTrackingNew.dayCard.assignCostCenter')}
                  onClick={() => dispatch({ type: 'assignCostCenter' })}
                />
              )}

              {showAddNewTimeEntryButton && (
                <ButtonLink
                  className="ms-3"
                  title={t('timeTrackingNew.dayCard.addNewTimeEntry')}
                  onClick={onAddNewTimeEntryClick}
                />
              )}

              {showConnectToProjectsButton && (
                <ButtonLink
                  className="ms-3"
                  title={t('timeTrackingNew.dayCard.connectToProjects')}
                  onClick={onConnectToProjects}
                />
              )}
            </>
          )}
        </div>
        <div className="d-flex align-items-center">
          {isFixBreaksVisible && (
            <ButtonLink
              className="me-3"
              title={t('timeTrackingNew.dayCard.fixBreaks')}
              onClick={() =>
                dispatch({ type: 'autoAdjust', breakRules: autoBreakRules })
              }
            />
          )}
          <TimeEntryWorkingTimeIndicator
            timeEntryId={timeEntry.uniqueId}
            timeStats={timeEntryStats}
            breaksDeductionStatus={timeEntry.breaksDeductionStatus}
            projectTimeEntriesMinutes={
              isTimeTrackingWithProjects
                ? currentProjectTimeEntriesMinutes !== null
                  ? currentProjectTimeEntriesMinutes
                  : totalProjectTimeEntriesMinutes
                : 0
            }
          />
        </div>
      </div>

      {isTimeTrackingWithProjects && canShowProjectTimeEntries && (
        <div className="mt-3">
          <DayProjectTimeEntriesForm
            ref={projectEntriesFormRef}
            day={new Date(timeEntry.start.date)}
            disabled={readonly || isSavingProjectTimeEntries}
            disableSubmit={isSavingProjectTimeEntries}
            restrictedFutureEntries={restrictedFutureEntries ?? false}
            projects={projects ?? []}
            timesTracked={mappedProjectTimeEntries}
            showTimeEntries={showTimeEntries ?? false}
            timeEntries={timeEntriesForDay ?? []}
            currentTimeEntryId={timeEntry.timeEntryId}
            onSave={handleSaveProjectTimeEntries}
            compactMode={true}
            setDayTotalTracked={() => {}}
            onProjectTimeEntriesMinutesChange={
              handleProjectTimeEntriesMinutesChange
            }
            autoAddFirstEntry={
              !hasProjectTimeEntries && showProjectTimeEntriesForm
            }
            onCancel={onCancelProjectTimeEntries}
            initialHours={initialHours}
            showTimeEntryColumn={false}
            i18prefix="timeTracking.projects"
            noForm={true}
            onProjectEntriesChange={onProjectEntriesChange}
            resetKey={projectEntriesResetKey}
          />
        </div>
      )}
    </div>
  );
};

export default TimeEntryDumb;
